/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * ProfileComponent.tsx
 */

import {PolicyListener} from "../../services/PolicyService";
import {RouteComponentProps} from 'react-router-dom';
import Policy from "../../tools/provider/Policy";
import HiloReactComponent from "../../tools/view/layout/HiloReactComponent";
import React from "react";
import PageTitle, {TitleTab} from "../../tools/view/layout/PageTitle";
import Profile from "../../models/Profile";
import EditProfileForm from "../../models/form/profile/EditProfileForm";
import UIToast from "../../tools/view/toast/UIToast";
import KareviHTTPService from "../../tools/service/KareviHTTPService";
import {ListWatcher} from "../../tools/list/ListAdapter";
import FormSection from "../../tools/view/form/FormSection";
import {ProfileDTO} from "../../models/dto/profile/ProfileDTO";
import {AbstractForm, FormListener} from "../../tools/model/form/AbstractForm";
import PageComponent from "../../tools/view/layout/PageComponent";
import FormInput, {Type} from "../../tools/view/form/FormInput";
import {TextField} from "../../tools/model/form/TextField";
import ProfileService from "../../services/ProfileService";
import NavigationService from "../../tools/service/NavigationService";
import CatalogService from "../../services/CatalogService";

interface Props extends RouteComponentProps<MatchParams> {
}

interface MatchParams {
    id: string;
}

interface State {
    policy: Policy;
    pageIndex: number,
    profile?: Profile;
    profileForm: EditProfileForm;
}

export default class ProfilePage extends HiloReactComponent<Props, State> implements PolicyListener, ListWatcher, FormListener<AbstractForm<ProfileDTO>, ProfileDTO> {
    private readonly _profileService: ProfileService;
    private readonly _catalogService: CatalogService;
    private readonly _httpService: KareviHTTPService;
    private readonly _navigationService: NavigationService;
    private _modalContainer?: Element;

    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: Props, state: State) {
        super(props, state);

        this._profileService = this._serviceRepository.resolve(ProfileService.id) as ProfileService
        this._catalogService = this._serviceRepository.resolve(CatalogService.id) as CatalogService
        this._httpService = this._serviceRepository.resolve(KareviHTTPService.id) as KareviHTTPService
        this._navigationService = this._serviceRepository.resolve(NavigationService.id) as NavigationService

        this.state = {
            policy: this._policyService.policy,
            pageIndex: 0,
            profile: undefined,
            profileForm: new EditProfileForm([])
        };
    }

    /**
     * onUpdate
     */
    public onUpdate(): void {
        this.forceUpdate();
    }

    /**
     * componentDidMount
     */
    public componentDidMount(): void {
        super.componentDidMount();
        this._policyService.addListener(this);

        this._modalContainer = document.querySelector('#modal-container') || undefined;
        this.state.profileForm.addListener(this)

        this._catalogService.findAll().then(catalogs => {
            this.state.profileForm.loadCatalogs(catalogs);
        })

        this._refreshProfile();
    }

    /**
     * _refreshProfile
     */
    private _refreshProfile = async () => {
        let profile = await this._profileService.findById(this.props.match.params.id);

        await this.state.profileForm.load(ProfileDTO.fromProfile(profile));

        await this.setState({profile: profile});
    };

    public componentWillUnmount(): void {
        this.state.profileForm.removeListener(this);
        this._policyService.removeListener(this);
        super.componentWillUnmount();
    }

    /**
     * componentDidUpdate
     * @param prevProps
     * @param prevState
     * @param snapshot
     */
    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps.match.params.id !== this.props.match.params.id) {
            this._refreshProfile();
        }
    }


    /**
     * policyUpdated
     * @param policy
     */
    public policyUpdated = (policy: Policy) => {
        this.setState({
            policy: policy
        })
    };

    /**
     * _profileView
     * @returns {any}
     * @private
     */
    private _profileView = (props: { form: EditProfileForm, profile?: Profile }) => {
        let profileIdField = new TextField("profile_id", t => t.profileId, _ => true, Type.TEXT, false, props.profile?.id, "", true)
        let profileUuidField = new TextField("profile_uuid", t => t.uuid, _ => true, Type.TEXT, false, props.profile?.userId, "", true)

        return <>
            <FormInput field={profileIdField}/>
            <FormInput field={profileUuidField}/>
            <FormSection label={this.t.information} form={props.form} submit={this._saveProfile}/>
        </>
    };


    /**
     * _saveProfile
     * @param dto
     * @private
     */
    private _saveProfile = async (dto: Map<string, any> | FormData) => {
        const {profile} = this.state;
        if (!profile) return console.log("profile is undefined.");

        try {
            let data = this.state.profileForm.compile();
            await this._profileService.updateCatalogAttribution(profile, data);
            this.state.profileForm.statusCode = 200;
            UIToast.default.show(this.t.profileEdited)
            await this._refreshProfile();
        } catch (e) {
            this.state.profileForm.statusCode = e.status
            console.error(e);
        }
    };

    onFormUpdate(form: AbstractForm<ProfileDTO>): void {
        this.forceUpdate()
    }

    /**
     * render
     */
    public render(): any {
        const {profile, profileForm} = this.state;
        const titleTabs: TitleTab[] = [
            {
                title: t => t.details, content: <>
                    <this._profileView form={profileForm} profile={profile}/>
                </>
            },
        ];

        return <PageComponent>
            <PageTitle title={t => t.profiles}
                       tabs={titleTabs}
                       onTabSelected={index => this.setState({pageIndex: index})}
                       subTitle={profile?.name || this.t.loading}
                       backLabel={t => t.profiles}
            />
            {titleTabs[this.state.pageIndex].content}
        </PageComponent>
    }
}
